/**
 * PlayerSim: one racer's run through all levels of a race.
 *
 * Wraps the physics with the race rules: attempts and respawns, level transitions,
 * the boost inventory and active boost effects. Fully deterministic: the same
 * sequence of `tick(held)` / `useBoost(slot)` calls (indexed by tick) always gives
 * the same run, on every JavaScript engine.
 *
 * Time: every `tick()` is one fixed step of GAME time (1/TICK_RATE s). Boosts change
 * how fast game time flows for this player (`timeScale`); the client's loop should
 * run `timeScale × TICK_RATE` ticks per real second. Boost durations count game ticks.
 */
import { BOOSTS, type BoostType, boostBit, MAX_BOOST_SLOTS } from '../boosts';
import type { LevelData } from '../level/types';
import { LEVEL_TRANSITION_TICKS, MODE_BOXES, RESPAWN_DELAY_TICKS, TICK_RATE } from './constants';
import { isInsideSolid, isTouchingHazard, spawnPlayer, stepPlayer } from './physics';
import type { SimEvent } from './simEvents';
import { EXACT_PROFILE, type PhysicsEvent, type PhysicsModifiers, type PlayerState } from './types';
import { World } from './world';

/**
 * running = playing the level; dead = waiting to respawn (input ignored);
 * levelComplete = short pause before the next level; finished = race done.
 */
export type SimPhase = 'running' | 'dead' | 'levelComplete' | 'finished';

export interface ActiveBoost {
  readonly type: BoostType;
  /** Game ticks left. */
  readonly remaining: number;
  /** Full length in game ticks (for progress bars). */
  readonly duration: number;
}

export interface PlayerSimOptions {
  /** Level index to start from (e.g. after a reconnect). Default 0. */
  startLevel?: number;
}

interface MutableActiveBoost {
  type: BoostType;
  remaining: number;
  duration: number;
}

/** Length of a boost's effect in game ticks. */
export function boostDurationTicks(type: BoostType): number {
  return Math.max(1, Math.round(BOOSTS[type].durationSec * TICK_RATE));
}

export class PlayerSim {
  readonly levels: readonly LevelData[];

  private levelIdx: number;
  private currentWorld: World;
  private current: PlayerState;
  private currentPhase: SimPhase = 'running';
  private attemptNo = 1;
  /** Ticks left in the dead / levelComplete phases. */
  private phaseTimer = 0;
  private lastX: number;
  private lastY: number;
  private ticksTotal = 0;
  private ticksOnLevel = 0;
  private deathCount = 0;
  private readonly slots: Array<BoostType | null>;
  private readonly active: MutableActiveBoost[] = [];
  private readonly collected = new Set<number>();
  private readonly modifiers: PhysicsModifiers = { invulnerable: false, airJumps: 0 };
  /** Events produced between ticks (useBoost), delivered with the next tick. */
  private pending: SimEvent[] = [];
  private readonly physicsEvents: PhysicsEvent[] = [];

  constructor(levels: readonly LevelData[], options: PlayerSimOptions = {}) {
    if (levels.length === 0) throw new Error('PlayerSim needs at least one level');
    const startLevel = options.startLevel ?? 0;
    if (!Number.isInteger(startLevel) || startLevel < 0 || startLevel >= levels.length) {
      throw new RangeError(`startLevel ${startLevel} is outside 0..${levels.length - 1}`);
    }
    this.levels = levels;
    this.levelIdx = startLevel;
    this.currentWorld = new World(this.level);
    this.current = spawnPlayer(this.currentWorld, this.level.start);
    this.lastX = this.current.x;
    this.lastY = this.current.y;
    this.slots = new Array<BoostType | null>(MAX_BOOST_SLOTS).fill(null);
  }

  /** Index of the current level in `levels`. */
  get levelIndex(): number {
    return this.levelIdx;
  }

  get level(): LevelData {
    return this.levels[this.levelIdx]!;
  }

  get world(): World {
    return this.currentWorld;
  }

  /** Physical state of the current attempt. Read-only for callers. */
  get state(): Readonly<PlayerState> {
    return this.current;
  }

  get phase(): SimPhase {
    return this.currentPhase;
  }

  get finished(): boolean {
    return this.currentPhase === 'finished';
  }

  /** Attempt number on the current level, starting at 1. */
  get attempt(): number {
    return this.attemptNo;
  }

  /** Game ticks since the race start (all phases). */
  get raceTicks(): number {
    return this.ticksTotal;
  }

  /** Game ticks spent running the current level (all attempts). */
  get levelTicks(): number {
    return this.ticksOnLevel;
  }

  get deaths(): number {
    return this.deathCount;
  }

  /** Position before the last tick, for render interpolation (equals the current one after a respawn). */
  get prevX(): number {
    return this.lastX;
  }

  get prevY(): number {
    return this.lastY;
  }

  /** Progress through the current level, 0..1. */
  get progress(): number {
    const { start, length } = this.level;
    const span = length - start.x;
    return span > 0 ? Math.min(1, Math.max(0, (this.current.x - start.x) / span)) : 1;
  }

  /** Boost inventory; slots keep their position, a pickup fills the first empty slot. */
  get inventory(): ReadonlyArray<BoostType | null> {
    return this.slots;
  }

  get activeBoosts(): ReadonlyArray<ActiveBoost> {
    return this.active;
  }

  /** How fast game time flows for this player: product of the active boosts' timeScale. */
  get timeScale(): number {
    let scale = 1;
    for (const boost of this.active) scale *= BOOSTS[boost.type].timeScale ?? 1;
    return scale;
  }

  /** Object indices of the pickups collected during the current attempt (hide them). */
  get collectedPickups(): ReadonlySet<number> {
    return this.collected;
  }

  /** Active boosts as a bitmask (PlayerNetState.fx). */
  activeBoostBits(): number {
    let bits = 0;
    for (const boost of this.active) bits |= boostBit(boost.type);
    return bits;
  }

  isBoostActive(type: BoostType): boolean {
    return this.active.some((b) => b.type === type);
  }

  /**
   * Activates the boost in inventory `slot` (only while running). Re-using a boost
   * that is already active restarts its timer. Returns the activated boost, or null
   * if nothing happened. Its `boostStart` event comes with the next tick.
   */
  useBoost(slot: number): BoostType | null {
    if (this.currentPhase !== 'running') return null;
    const type = this.slots[slot];
    if (type == null) return null;
    this.slots[slot] = null;
    const duration = boostDurationTicks(type);
    const existing = this.active.find((b) => b.type === type);
    if (existing) existing.remaining = duration;
    else this.active.push({ type, remaining: duration, duration });
    this.updateModifiers();
    const airJumps = BOOSTS[type].airJumps ?? 0;
    if (airJumps > 0) this.current.airJumps = Math.max(this.current.airJumps, airJumps);
    this.pending.push({ type: 'boostStart', boost: type, slot });
    return type;
  }

  /** Advances one game tick with the given input. Returns the events of this tick (a new array). */
  tick(held: boolean): SimEvent[] {
    const out = this.pending;
    this.pending = [];
    if (this.currentPhase === 'finished') return out;
    this.ticksTotal++;
    this.lastX = this.current.x;
    this.lastY = this.current.y;
    switch (this.currentPhase) {
      case 'running':
        this.runTick(held, out);
        break;
      case 'dead':
        if (--this.phaseTimer <= 0) this.restartLevel(out);
        break;
      case 'levelComplete':
        if (--this.phaseTimer <= 0) this.startLevel(this.levelIdx + 1, out);
        break;
    }
    this.updateBoosts(out);
    return out;
  }

  private runTick(held: boolean, out: SimEvent[]): void {
    this.ticksOnLevel++;
    const events = this.physicsEvents;
    events.length = 0;
    stepPlayer(this.currentWorld, this.current, held, this.modifiers, EXACT_PROFILE, events);
    for (const e of events) {
      if (e.type === 'pickup') {
        this.collected.add(e.object);
        const slot = this.slots.indexOf(null);
        if (slot >= 0) this.slots[slot] = e.boost;
        out.push({ type: 'pickup', boost: e.boost, object: e.object, ignored: slot < 0 });
      } else if (e.type !== 'finish') {
        out.push(e);
      }
    }
    if (!this.current.alive) {
      this.deathCount++;
      this.currentPhase = 'dead';
      this.phaseTimer = RESPAWN_DELAY_TICKS;
      this.endAllBoosts(out);
    } else if (this.current.finished) {
      out.push({
        type: 'levelComplete',
        level: this.levelIdx,
        attempts: this.attemptNo,
        ticks: this.ticksOnLevel,
      });
      if (this.levelIdx === this.levels.length - 1) {
        this.currentPhase = 'finished';
        this.endAllBoosts(out);
        out.push({ type: 'raceFinished', ticks: this.ticksTotal, deaths: this.deathCount });
      } else {
        this.currentPhase = 'levelComplete';
        this.phaseTimer = LEVEL_TRANSITION_TICKS;
      }
    }
  }

  private restartLevel(out: SimEvent[]): void {
    this.attemptNo++;
    this.resetRun();
    out.push({ type: 'respawn', attempt: this.attemptNo });
  }

  private startLevel(index: number, out: SimEvent[]): void {
    this.levelIdx = index;
    this.currentWorld = new World(this.level);
    this.attemptNo = 1;
    this.ticksOnLevel = 0;
    this.resetRun();
    out.push({ type: 'levelStart', level: index });
  }

  /** New attempt at the level start; pickups respawn, the inventory is kept. */
  private resetRun(): void {
    this.current = spawnPlayer(this.currentWorld, this.level.start);
    this.current.airJumps = this.modifiers.airJumps;
    this.collected.clear();
    this.currentPhase = 'running';
    this.lastX = this.current.x;
    this.lastY = this.current.y;
  }

  private updateBoosts(out: SimEvent[]): void {
    if (this.active.length === 0) return;
    let changed = false;
    for (let i = 0; i < this.active.length; ) {
      const boost = this.active[i]!;
      boost.remaining--;
      if (boost.remaining > 0) {
        i++;
        continue;
      }
      // Never kill the player the instant a shield runs out: keep it until they are free.
      if (BOOSTS[boost.type].invulnerable && this.currentPhase === 'running' && this.isStuck()) {
        boost.remaining = 1;
        i++;
        continue;
      }
      this.active.splice(i, 1);
      changed = true;
      out.push({ type: 'boostEnd', boost: boost.type });
    }
    if (changed) this.updateModifiers();
  }

  private endAllBoosts(out: SimEvent[]): void {
    for (const boost of this.active) out.push({ type: 'boostEnd', boost: boost.type });
    this.active.length = 0;
    this.updateModifiers();
  }

  private updateModifiers(): void {
    let invulnerable = false;
    let airJumps = 0;
    for (const boost of this.active) {
      const def = BOOSTS[boost.type];
      invulnerable ||= def.invulnerable === true;
      airJumps = Math.max(airJumps, def.airJumps ?? 0);
    }
    this.modifiers.invulnerable = invulnerable;
    this.modifiers.airJumps = airJumps;
    this.current.airJumps = Math.min(this.current.airJumps, airJumps);
  }

  /** Inside a wall, touching a spike, or below the ground in a pit (where only the shield saves). */
  private isStuck(): boolean {
    const s = this.current;
    const belowGround = s.gravity > 0 && !s.grounded && s.y - MODE_BOXES[s.mode].outer.h / 2 < 0;
    return (
      belowGround || isInsideSolid(this.currentWorld, s) || isTouchingHazard(this.currentWorld, s)
    );
  }
}
